import React, { useState, useEffect } from 'react';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from './firebase';
import { getApprovedQuotes } from './services/db';
import { trackSoftwareViewed } from './services/analytics';
import { X, ExternalLink, DollarSign, Users, Calendar, Tag, Info, TrendingUp, AlertCircle, MessageSquare } from 'lucide-react';
import './SoftwareDetail.css';

/**
 * SoftwareDetail - Modular component for displaying detailed software pricing information
 *
 * This component is designed to be flexible and handle various pricing structures.
 * See SOFTWARE_DETAIL_GUIDE.md for documentation on the data structure.
 */
const SoftwareDetail = ({ software, onClose }) => {
  const [liveQuotes, setLiveQuotes] = useState([]);
  const [liveSummary, setLiveSummary] = useState(undefined); // undefined = not yet loaded
  const [loadingQuotes, setLoadingQuotes] = useState(true);

  useEffect(() => {
    if (!software?.id) {
      setLoadingQuotes(false);
      return;
    }

    trackSoftwareViewed(software.name, software.category);

    // Real-time listener for pricingSummary on the software doc
    const unsubscribe = onSnapshot(doc(db, 'software', software.id), (snap) => {
      if (snap.exists()) {
        setLiveSummary(snap.data().pricingSummary || null);
      }
    }, (error) => {
      console.error('Error listening to software doc:', error);
    });

    // Fetch approved quotes
    setLoadingQuotes(true);
    getApprovedQuotes(software.id)
      .then(quotes => setLiveQuotes(quotes))
      .catch(err => console.error('Error fetching quotes:', err))
      .finally(() => setLoadingQuotes(false));

    return () => unsubscribe();
  }, [software?.id]);

  if (!software) return null;

  const {
    name,
    website,
    category,
    description,
    logo,
    // Pricing data - all optional, component adapts to what's available
    pricingModel,      // 'per-seat', 'per-user', 'flat', 'usage', 'custom', 'hybrid'
    billingCycles,     // Array of available billing options
    tiers,             // Array of pricing tiers
    addOns,            // Array of add-on products/features
    volumeDiscounts,   // Volume discount information
    enterpriseContact, // Whether enterprise requires contact
    pricingSummary,    // Aggregated community pricing data (static fallback)
    notes,             // General notes about pricing
    lastUpdated,       // When pricing was last verified
  } = software;

  // Use live summary if loaded, fall back to static pricingSummary
  const activeSummary = liveSummary !== undefined ? liveSummary : pricingSummary;

  // Last contributed date = most recent approved quote's submittedAt
  const lastContributed = liveQuotes.length > 0
    ? liveQuotes[0].submittedAt?.toDate?.() || null
    : null;

  // Compact community stats bar used inside tier and add-on cards
  const renderCommunityStats = (stats) => {
    if (!stats || stats.quoteCount === 0) return null;
    return (
      <div className="detail-card-community-stats">
        <span className="detail-card-stats-count">{stats.quoteCount} {stats.quoteCount === 1 ? 'quote' : 'quotes'}</span>
        {stats.avgTotal != null && (
          <span className="detail-card-stats-item">avg total: <strong>${stats.avgTotal.toLocaleString()}</strong></span>
        )}
        {stats.avgPerSeat != null && (
          <span className="detail-card-stats-item">avg/seat: <strong>${stats.avgPerSeat.toLocaleString()}</strong></span>
        )}
      </div>
    );
  };

  // Render pricing tier card
  const renderTier = (tier, index) => {
    const tierStats = activeSummary?.byTier?.[tier.name.toLowerCase()] || null;
    return (
      <div key={index} className={`detail-tier-card ${tier.enterprise ? 'enterprise' : ''}`}>
        {tier.enterprise && <span className="detail-tier-badge enterprise">Enterprise</span>}

        <h4 className="detail-tier-name">{tier.name}</h4>

        {tier.price !== undefined && tier.price !== null ? (
          <div className="detail-tier-price">
            <span className="detail-tier-amount">
              {tier.price === 0 ? 'Free' : `$${tier.price.toLocaleString()}`}
            </span>
            {tier.price > 0 && tier.unit && (
              <span className="detail-tier-unit">/{tier.unit}</span>
            )}
            {tier.billing && (
              <span className="detail-tier-billing">{tier.billing}</span>
            )}
          </div>
        ) : (
          <div className="detail-tier-price">
            <span className="detail-tier-custom">Contact Sales</span>
          </div>
        )}

        {tier.description && (
          <p className="detail-tier-description">{tier.description}</p>
        )}

        {tier.features && tier.features.length > 0 && (
          <ul className="detail-tier-features">
            {tier.features.map((feature, i) => (
              <li key={i} className={feature.included === false ? 'not-included' : ''}>
                {typeof feature === 'string' ? feature : feature.name}
              </li>
            ))}
          </ul>
        )}

        {tier.limits && (
          <div className="detail-tier-limits">
            {Object.entries(tier.limits).map(([key, value]) => (
              <span key={key} className="detail-tier-limit">
                {key}: {value}
              </span>
            ))}
          </div>
        )}

        {renderCommunityStats(tierStats)}
      </div>
    );
  };

  // Render add-on item
  const renderAddOn = (addOn, index) => {
    const addOnStats = activeSummary?.byAddOn?.[addOn.name.toLowerCase()] || null;
    return (
      <div key={index} className="detail-addon-item">
        <div className="detail-addon-info">
          <span className="detail-addon-name">{addOn.name}</span>
          {addOn.description && (
            <span className="detail-addon-description">{addOn.description}</span>
          )}
          {renderCommunityStats(addOnStats)}
        </div>
        <div className="detail-addon-price">
          {addOn.price != null ? (
            <>
              <span>${addOn.price.toLocaleString()}</span>
              {addOn.unit && <span className="detail-addon-unit">/{addOn.unit}</span>}
            </>
          ) : (
            <span className="detail-addon-custom">Contact Sales</span>
          )}
        </div>
      </div>
    );
  };

  // Render a live community quote from Firestore (data nested under .data)
  const renderQuote = (quote, index) => {
    const d = quote.data || {};
    const displayDate = d.quoteDate
      ? new Date(d.quoteDate).toLocaleDateString()
      : quote.submittedAt?.toDate?.()
      ? quote.submittedAt.toDate().toLocaleDateString()
      : 'Date unknown';

    return (
      <div key={quote.id || index} className="detail-quote-card">
        <div className="detail-quote-header">
          <span className="detail-quote-date">{displayDate}</span>
        </div>
        <div className="detail-quote-details">
          {d.tier && (
            <div className="detail-quote-item">
              <Tag size={14} />
              <span>{d.tier}</span>
            </div>
          )}
          {d.seats && (
            <div className="detail-quote-item">
              <Users size={14} />
              <span>{d.seats} seats</span>
            </div>
          )}
          {d.term && (
            <div className="detail-quote-item">
              <Calendar size={14} />
              <span>{d.term}</span>
            </div>
          )}
        </div>
        {d.totalPrice != null && (
          <div className="detail-quote-price">
            <DollarSign size={18} />
            <span className="detail-quote-amount">${d.totalPrice.toLocaleString()}</span>
            {d.seats > 0 && (
              <span className="detail-quote-per-seat">
                (${Math.round(d.totalPrice / d.seats).toLocaleString()}/seat)
              </span>
            )}
          </div>
        )}
        {d.notes && (
          <p className="detail-quote-notes">{d.notes}</p>
        )}
      </div>
    );
  };

  return (
    <div className="software-detail">
      {/* Header */}
      <div className="detail-header">
        <div className="detail-header-left">
          {logo && (
            <img src={logo} alt={`${name} logo`} className="detail-logo" />
          )}
          <div className="detail-header-info">
            <h2 className="detail-title">{name}</h2>
            {category && <span className="detail-category">{category}</span>}
          </div>
        </div>
        <div className="detail-header-right">
          {website && (
            <a
              href={`https://${website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="detail-website-btn"
            >
              <ExternalLink size={16} />
              Visit Website
            </a>
          )}
          <button className="detail-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="detail-content">
        {/* Description */}
        {description && (
          <div className="detail-section">
            <p className="detail-description">{description}</p>
          </div>
        )}

        {/* Pricing Summary - Community Data */}
        <div className="detail-section">
          <h3 className="detail-section-title">
            <TrendingUp size={18} />
            Community Pricing Data
            {activeSummary?.quoteCount > 0 && (
              <span className="detail-quote-count-badge">
                {activeSummary.quoteCount} {activeSummary.quoteCount === 1 ? 'quote' : 'quotes'}
              </span>
            )}
          </h3>
          {activeSummary && activeSummary.quoteCount > 0 ? (
            <>
              <div className="detail-summary-grid">
                {activeSummary.avgPerSeat && (
                  <div className="detail-summary-card">
                    <span className="detail-summary-label">Avg. Per Seat</span>
                    <span className="detail-summary-value">${activeSummary.avgPerSeat.toLocaleString()}</span>
                    {activeSummary.perSeatRange && (
                      <span className="detail-summary-range">
                        Range: ${activeSummary.perSeatRange[0]} - ${activeSummary.perSeatRange[1]}
                      </span>
                    )}
                  </div>
                )}
                {activeSummary.avgTotal && (
                  <div className="detail-summary-card">
                    <span className="detail-summary-label">Avg. Total</span>
                    <span className="detail-summary-value">${activeSummary.avgTotal.toLocaleString()}</span>
                  </div>
                )}
                {activeSummary.commonDiscount && (
                  <div className="detail-summary-card">
                    <span className="detail-summary-label">Common Discount</span>
                    <span className="detail-summary-value">{activeSummary.commonDiscount}%</span>
                  </div>
                )}
              </div>
              {lastContributed && (
                <p className="detail-last-contributed">
                  Last contributed: {lastContributed.toLocaleDateString()}
                </p>
              )}
            </>
          ) : (
            <div className="detail-community-empty">
              <MessageSquare size={32} />
              <p>No community data yet — be the first to submit a quote!</p>
            </div>
          )}
        </div>

        {/* Pricing Model Info */}
        {pricingModel && (
          <div className="detail-section">
            <h3 className="detail-section-title">
              <Info size={18} />
              Pricing Model
            </h3>
            <div className="detail-pricing-model">
              <span className="detail-model-badge">{pricingModel}</span>
              {billingCycles && billingCycles.length > 0 && (
                <div className="detail-billing-info">
                  <span className="detail-billing-label">Billing:</span>
                  {billingCycles.map((cycle, i) => (
                    <span key={i} className="detail-billing-option">
                      {cycle.name}{cycle.discount ? ` (${cycle.discount}% off)` : ''}
                      {i < billingCycles.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pricing Tiers */}
        {tiers && tiers.length > 0 && (
          <div className="detail-section">
            <h3 className="detail-section-title">
              <DollarSign size={18} />
              Pricing Tiers
            </h3>
            <div className="detail-tiers-grid">
              {tiers.map(renderTier)}
            </div>
          </div>
        )}

        {/* Add-ons */}
        {addOns && addOns.length > 0 && (
          <div className="detail-section">
            <h3 className="detail-section-title">
              <Tag size={18} />
              Add-ons & Extras
            </h3>
            <div className="detail-addons-list">
              {addOns.map(renderAddOn)}
            </div>
          </div>
        )}

        {/* Volume Discounts */}
        {volumeDiscounts && (
          <div className="detail-section">
            <h3 className="detail-section-title">
              <Users size={18} />
              Volume Discounts
            </h3>
            <div className="detail-volume-table">
              <div className="detail-volume-header">
                <span>Seats/Units</span>
                <span>Discount</span>
              </div>
              {volumeDiscounts.tiers?.map((tier, i) => (
                <div key={i} className="detail-volume-row">
                  <span>{tier.min}{tier.max ? `-${tier.max}` : '+'}</span>
                  <span className="detail-volume-discount">{tier.discount}% off</span>
                </div>
              ))}
              {volumeDiscounts.notes && (
                <p className="detail-volume-notes">{volumeDiscounts.notes}</p>
              )}
            </div>
          </div>
        )}

        {/* Community Quotes - Live from Firestore */}
        <div className="detail-section">
          <h3 className="detail-section-title">
            <Users size={18} />
            Community Quotes
            {liveQuotes.length > 0 && ` (${liveQuotes.length})`}
          </h3>
          {loadingQuotes ? (
            <p className="detail-loading">Loading quotes...</p>
          ) : liveQuotes.length > 0 ? (
            <div className="detail-quotes-grid">
              {liveQuotes.map(renderQuote)}
            </div>
          ) : (
            <p className="detail-no-quotes">No approved quotes yet.</p>
          )}
        </div>

        {/* Notes */}
        {notes && (
          <div className="detail-section">
            <h3 className="detail-section-title">
              <AlertCircle size={18} />
              Pricing Notes
            </h3>
            <div className="detail-notes">
              {Array.isArray(notes) ? (
                <ul>
                  {notes.map((note, i) => <li key={i}>{note}</li>)}
                </ul>
              ) : (
                <p>{notes}</p>
              )}
            </div>
          </div>
        )}

        {/* Enterprise Contact */}
        {enterpriseContact && (
          <div className="detail-enterprise-cta">
            <p>Need enterprise pricing or custom terms?</p>
            <a
              href={`https://${website}/contact`}
              target="_blank"
              rel="noopener noreferrer"
              className="detail-enterprise-btn"
            >
              Contact {name} Sales
            </a>
          </div>
        )}

        {/* Last Updated */}
        {lastUpdated && (
          <div className="detail-footer">
            <span className="detail-updated">
              Pricing last verified: {new Date(lastUpdated).toLocaleDateString()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoftwareDetail;
